/**
 * Batch Export filename helpers.
 *
 * Kept free of imports so they can be unit tested directly with node:test.
 */

/**
 * Sanitizes a string so it is safe to use as (part of) a filename.
 */
export function sanitizeFileNamePart(value) {
    return String(value ?? "")
        .replace(/[\\/:*?"<>|]/g, "_") // characters illegal in filenames
        .replace(/[\u0000-\u001f]/g, "") // control characters
        .replace(/\s+/g, " ")
        .trim() || "untitled";
}

/**
 * Builds the output filename for each preset exported from one Event Group.
 * Returns a map of presetId -> fileName.
 *
 * - An extension used by a single preset keeps the plain <groupName>.<ext> name.
 * - An extension shared by several presets becomes <groupName>_<presetName>.<ext>
 *   so one file does not overwrite another.
 * - Names that still match (case-insensitive) get " (2)", " (3)"... before the extension.
 */
export function buildBatchExportFileNames(groupName, presets) {
    const validPresets = (presets || []).filter((preset) => preset && preset.config);
    const extCounts = {};
    validPresets.forEach((preset) => {
        const extKey = String(preset.config.trgExt ?? "").toLowerCase();
        extCounts[extKey] = (extCounts[extKey] || 0) + 1;
    });

    const usedNames = new Set();
    const fileNames = {};
    validPresets.forEach((preset) => {
        const ext = preset.config.trgExt;
        const extKey = String(ext ?? "").toLowerCase();
        const baseName = extCounts[extKey] > 1
            ? groupName + "_" + sanitizeFileNamePart(preset.name)
            : groupName;

        let fileName = baseName + "." + ext;
        let copy = 2;
        while (usedNames.has(fileName.toLowerCase())) {
            fileName = baseName + " (" + copy + ")." + ext;
            copy++;
        }

        usedNames.add(fileName.toLowerCase());
        fileNames[preset.id] = fileName;
    });

    return fileNames;
}
